import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import Refresh from '@mui/icons-material/Refresh';
import ExploreIcon from '@mui/icons-material/Explore';
import { green, red, grey } from '@mui/material/colors';
import * as api from '../../api';
import CountrySelect from '../../components/CountrySelect';

const LEVEL_FILTER_KEY = '__app_discover_level_filter';
const GEO_FILTER_KEY = '__app_discover_geo_filter';

// How far ahead the "Upcoming" section looks.
const UPCOMING_DAYS = 14;

function matchDate(m) {
  return new Date(m.startDate.year, m.startDate.month - 1, m.startDate.day);
}

function sortByDate(matches) {
  return [...matches].sort((a, b) => matchDate(b) - matchDate(a));
}

export default function Discover() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(() => {
    const saved = localStorage.getItem(LEVEL_FILTER_KEY);
    return saved !== null ? Number(saved) : 1;
  });
  const [countryCode, setCountryCode] = useState(() => localStorage.getItem(GEO_FILTER_KEY) || '');
  const [countryDialogOpen, setCountryDialogOpen] = useState(false);
  const [tempCountry, setTempCountry] = useState('');

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getMatchesPublic();
      const matchList = Object.keys(data).map((k) => data[k]);
      setMatches(matchList);
    } catch (err) {
      console.error('Failed to fetch matches', err);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  useEffect(() => { localStorage.setItem(LEVEL_FILTER_KEY, filter); }, [filter]);
  useEffect(() => { localStorage.setItem(GEO_FILTER_KEY, countryCode); }, [countryCode]);

  const filtered = useMemo(() => {
    if (!matches) return [];
    return sortByDate(
      matches
        .filter((m) => (m.ipscLevel || 0) >= filter)
        .filter((m) => !countryCode || m.countryCode === countryCode)
    );
  }, [matches, filter, countryCode]);

  // Matches starting within the next two weeks, soonest first.
  const upcoming = useMemo(() => {
    const today = moment().startOf('day');
    const horizon = moment().startOf('day').add(UPCOMING_DAYS, 'days').endOf('day');
    return filtered
      .filter((m) => {
        if (!m.startDate) return false;
        const d = moment([m.startDate.year, m.startDate.month - 1, m.startDate.day]);
        return d.isSameOrAfter(today) && d.isSameOrBefore(horizon);
      })
      .sort((a, b) => matchDate(a) - matchDate(b));
  }, [filtered]);

  const handleToggleFilter = () => setFilter((prev) => (prev + 1) % 2);

  const openCountryDialog = () => {
    setTempCountry(countryCode);
    setCountryDialogOpen(true);
  };

  const handleCountryOk = () => {
    setCountryCode(tempCountry);
    setCountryDialogOpen(false);
  };

  const renderMatchCard = (m) => {
    const date = moment([m.startDate.year, m.startDate.month - 1, m.startDate.day]).format('LL');
    const stagesCount = (m.stages || []).length;
    const level = m.ipscLevel || 0;

    return (
      <Paper
        key={m.alias}
        sx={{ borderRadius: 2.5, overflow: 'hidden' }}
      >
        <ButtonBase
          onClick={() => navigate(`/${m.alias}`)}
          sx={{ display: 'block', width: '100%', textAlign: 'start' }}
        >
          {/* Cover */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 180,
              backgroundImage: `url("${m.cover}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.1) 100%)',
                zIndex: 1,
              },
            }}
          >
            {/* Level badge */}
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                bgcolor: level === 0 ? red[700] : green[700],
                color: 'white',
                zIndex: 2,
                px: 1.5,
                py: 0.375,
                borderRadius: 1.5,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {t(`app.ipsclevel${level}`)}
            </Box>

            {/* Title + description */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                zIndex: 2,
                color: 'white',
              }}
            >
              <Typography variant="subtitle1" sx={{ color: 'inherit', fontWeight: 700, lineHeight: 1.2 }}>
                {m.title}
              </Typography>
              {m.description && (
                <Typography variant="body2" sx={{ color: 'inherit', mt: 0.25, opacity: 0.85, fontSize: 12 }}>
                  {m.description}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Info bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', py: 1, px: 1.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {date}
            </Typography>
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: grey[400], mx: 1 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {stagesCount} {t('app.stages')}
            </Typography>
          </Box>
        </ButtonBase>
      </Paper>
    );
  };

  const sectionHeaderSx = {
    display: 'block',
    px: 0.5,
    mt: 1.5,
    mb: 0.5,
    fontWeight: 700,
    color: 'text.secondary',
    lineHeight: 1.6,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: '#f5f5f5' }}>
      {/* Hero header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
        color: 'white',
        px: 2, pt: 2, pb: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('app.discover')}
          </Typography>
          <IconButton onClick={fetchMatches} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <Refresh />
          </IconButton>
        </Box>
        {!loading && matches && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
            {filtered.length} {filtered.length === 1 ? 'match' : 'matches'}
          </Typography>
        )}

        {/* Filter chips */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Chip
            label={filter === 0 ? t('app.filterAll') : t('app.filterApproved')}
            size="small"
            onClick={handleToggleFilter}
            sx={{
              bgcolor: filter === 1 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)',
              color: 'white',
              fontWeight: 600,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            }}
          />
          <Chip
            label={countryCode || t('app.geoFilterAll')}
            size="small"
            onClick={openCountryDialog}
            sx={{
              bgcolor: countryCode ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)',
              color: 'white',
              fontWeight: 600,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            }}
          />
        </Box>
      </Box>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
          <CircularProgress thickness={2} size={64} />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%', bgcolor: grey[200],
            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
          }}>
            <ExploreIcon sx={{ fontSize: 32, color: grey[400] }} />
          </Box>
          <Typography color="text.secondary">No results found</Typography>
        </Box>
      ) : (
        <Box sx={{ px: 1.5, pt: 0.5, pb: 1 }}>
          {upcoming.length > 0 && (
            <>
              <Typography variant="overline" sx={sectionHeaderSx}>
                {t('app.sectionUpcoming')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {upcoming.map(renderMatchCard)}
              </Box>
            </>
          )}

          <Typography variant="overline" sx={sectionHeaderSx}>
            {t('app.sectionAll')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filtered.map(renderMatchCard)}
          </Box>
        </Box>
      )}

      {/* Country filter dialog */}
      <Dialog open={countryDialogOpen} onClose={() => setCountryDialogOpen(false)} fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>{t('app.pickRegion')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('app.pickRegionDescription')}
          </Typography>
          <CountrySelect
            value={tempCountry}
            onChange={setTempCountry}
            label={t('app.pickRegion')}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTempCountry(''); setCountryCode(''); setCountryDialogOpen(false); }}>
            {t('app.geoFilterAll')}
          </Button>
          <Button onClick={() => setCountryDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCountryOk} variant="contained" sx={{ borderRadius: 2 }}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
